import {
  computed, Ref, watch, ref, ComputedRef,
} from 'vue'
import type { SelectModeType } from '@xhs/yam-beer/cmp/constants'
import { Dayjs } from 'dayjs'
import { formatDate, dayjs, isDayEqual } from '@xhs/yam-beer/utils/dayjs'
import { getTimeObjFromString } from '@xhs/yam-beer/utils/clockUtils'

export type TimeObjType = {
  hour: number
  minute: number
  second: number
}
export type Model = string | Dayjs | null | undefined
export type State = Dayjs | null
export type DayToString = (dayObj: State, mode: SelectModeType) => string

function defaultDayToString(dayObj: State, mode: SelectModeType) {
  if (!dayObj) {
    return ''
  }
  if (mode === 'year') {
    return formatDate(dayObj, 'YYYY')
  }
  if (mode === 'month') {
    return formatDate(dayObj, 'YYYY-MM')
  }
  if (mode === 'time') {
    return formatDate(dayObj, 'YYYY-MM-DD HH:mm:ss')
  }
  return formatDate(dayObj, 'YYYY-MM-DD')
}

type UseTime = {
  timeState: ComputedRef<TimeObjType | null>
  setTime: (day: Ref<State>, timeObj: TimeObjType) => Dayjs | null
}

export function useTimePart(dayState: Ref<State>, defaultTimeObj: TimeObjType | null): UseTime {
  const timeState = computed(() => {
    if (!dayState.value) return defaultTimeObj
    const state = {
      hour: dayState.value?.hour(),
      minute: dayState.value?.minute(),
      second: dayState.value?.second(),
    }
    return state
  })
  return {
    timeState,
    setTime(d, timeObj) {
      let day = d.value
      if (!day) {
        day = dayjs()
      }
      return day.hour(timeObj.hour)
        .minute(timeObj.minute)
        .second(timeObj.second)
    },
  }
}

function defaultToModel(state: State, mode: SelectModeType) {
  if (!state) {
    return null
  }
  if (mode === 'time') {
    return state.format('YYYY-MM-DD HH:mm:ss')
  }
  return state.format('YYYY-MM-DD')
}

function defaultToState(model: Model) {
  if (!model) {
    return null
  }
  const d = dayjs(model)
  if (d.isValid()) {
    return d
  }
  if (process.env.NODE_ENV !== 'production') {
    console.error('你传了一个错误的 model 值 🤜', model)
  }
  return null
}

type ParamsBase = {
  selectMode: SelectModeType
  defaultTime?: string
  toModel?: (state: State, mode?: SelectModeType) => Model
  toState?: (model: Model, mode?: SelectModeType) => State
  dayToString?: (state: State, mode?: SelectModeType) => string
}

type UseDateParams = Pick<
ParamsBase,
'selectMode' | 'dayToString' | 'defaultTime' | 'toModel' | 'toState'
> & {
  modelRef: Ref<Model>
}

type UseDateHooks = {
  base: Ref<Dayjs>
  dayState: Ref<State>
  dayStr: Ref<string>
  setModel: (d: Dayjs|null) => Model
  defaultTimeObj: TimeObjType
}

export function useDateValue(
  {
    modelRef,
    selectMode,
    toModel = defaultToModel,
    toState = defaultToState,
    dayToString = defaultDayToString,
    defaultTime = '12:00:00',
  }: UseDateParams,
): UseDateHooks {
  const defaultTimeObj = getTimeObjFromString(defaultTime)
  const dayState = computed(() => toState(modelRef.value, selectMode))
  const dayStr = computed(() => dayToString(dayState.value, selectMode))
  const base = computed(() => {
    if (!dayState.value) {
      return dayjs()
        .hour(defaultTimeObj.hour)
        .minute(defaultTimeObj.minute)
        .second(defaultTimeObj.second)
    }
    return dayState.value
  })
  const setModel = (obj: Dayjs|null) => {
    if (!obj) {
      return toModel(null, selectMode)
    }
    return toModel(obj, selectMode)
  }

  return {
    base,
    dayState,
    dayStr,
    setModel,
    defaultTimeObj,
  }
}

type UseDateListParams = Pick<
ParamsBase,
'selectMode' | 'dayToString' | 'defaultTime' | 'toModel' | 'toState'
> & {
  modelRef: Ref<Model[]|undefined>
}

type UseDateListHooks = {
  base: Ref<Dayjs>
  dayStateList: Ref<State[]>
  dayStrList: Ref<string[]>
  setModel: (d: Dayjs|null) => Model[]
}

export function useDateValueList(
  {
    modelRef,
    selectMode,
    toModel = defaultToModel,
    toState = defaultToState,
    dayToString = defaultDayToString,
    defaultTime = '12:00:00',
  }: UseDateListParams,
): UseDateListHooks {
  const defaultTimeObj = getTimeObjFromString(defaultTime)
  const dayStateList = computed(() => (modelRef.value || []).map(i => toState(i, selectMode)))
  const dayStrList = computed(() => dayStateList.value.map(i => dayToString(i, selectMode)))
  const base = computed(() => {
    if (!dayStateList.value || dayStateList.value.length < 1) {
      return dayjs()
        .hour(defaultTimeObj.hour)
        .minute(defaultTimeObj.minute)
        .second(defaultTimeObj.second)
    }
    return (dayStateList.value[dayStateList.value.length - 1] as Dayjs)
  })
  const setModel = (obj: Dayjs|null) => {
    if (!obj) {
      return []
    }
    let newDayList
    if (dayStateList.value.some(i => isDayEqual(obj, i))) {
      newDayList = dayStateList.value.filter(i => !isDayEqual(obj, i))
    } else {
      newDayList = dayStateList.value.concat([obj])
    }
    return newDayList.map(i => toModel(i, selectMode))
  }

  return {
    base,
    dayStateList,
    dayStrList,
    setModel,
  }
}

type UseDateRangeParams = Pick<
ParamsBase,
'selectMode' | 'dayToString' | 'toModel' | 'toState'
> & {
  startRef: Ref<Model|undefined>
  endRef: Ref<Model|undefined>
  defaultTimeList: string[]
  allowEqual: boolean
}

type RangeValue = {
  start: Model
  end: Model
}
type TimeState = {
  value: Readonly<{
    hour: number
    minute: number
    second: number
  }>
  setObj: (v: TimeObjType) => void
}
type UseDateRangeHooks = {
  base: Ref<Dayjs>
  startState: Ref<State>
  endState: Ref<State>
  startStr: Ref<string>
  endStr: Ref<string>
  setModel: (d: Dayjs|null) => RangeValue|null
  toModel: NonNullable<ParamsBase['toModel']>
  toRangeModel:(start: Dayjs, end: Dayjs) => RangeValue
  startTime: TimeState
  endTime: TimeState
}

function useTimeObj(defaultStr: string) {
  const timeObj = ref<TimeObjType>(getTimeObjFromString(defaultStr))
  return {
    get value() {
      return timeObj.value
    },
    setObj: (v: TimeObjType) => {
      timeObj.value = {
        hour: v.hour,
        minute: v.minute,
        second: v.second,
      }
    },
  }
}

export function useDateValueRange(
  {
    startRef,
    endRef,
    selectMode,
    allowEqual,
    dayToString = defaultDayToString,
    toModel = defaultToModel,
    toState = defaultToState,
    defaultTimeList = ['00:00:00', '23:59:59'],
  }: UseDateRangeParams,
): UseDateRangeHooks {
  const startTime = useTimeObj(defaultTimeList[0])
  const endTime = useTimeObj(defaultTimeList[1])
  const startState = computed(() => toState(startRef.value, selectMode))
  const startStr = computed(() => dayToString(startState.value, selectMode))
  const endState = computed(() => toState(endRef.value, selectMode))
  const endStr = computed(() => dayToString(endState.value, selectMode))

  const base = ref(dayjs())
  watch([startState, endState], ([start, end]) => {
    // 结束日期应展示在第二个面板中，否则仍以上一次的base展示当前面板时间
    if (start && end && start.month() !== end.month()) {
      base.value = end.subtract(1, 'month')
        .hour(0).minute(0).second(0)
    }
    // 当start选中为右侧面板中的时间时此时start和当前base的月份相差一个月 无需重置base
    if (start && !end && (start.month() - base.value.month() !== 1)) {
      base.value = dayjs(start)
        .hour(0).minute(0).second(0)
    }
    if (end && !start) {
      base.value = dayjs(end)
        .hour(0).minute(0).second(0)
    }
  })

  function setTime(dayObj: Dayjs|null, timeParth: TimeObjType): Dayjs|null {
    if (!dayObj) {
      return null
    }
    return dayObj
      .hour(timeParth.hour)
      .minute(timeParth.minute)
      .second(timeParth.second)
  }

  function toRangeModel(start: Dayjs, end: Dayjs): RangeValue {
    return {
      start: toModel(
        setTime(start, startTime.value),
        selectMode,
      ),
      end: toModel(
        setTime(end, endTime.value),
        selectMode,
      ),
    }
  }

  const setModel = (dayObj: Dayjs|null): RangeValue|null => {
    if (!dayObj) {
      return { start: toModel(null, selectMode), end: toModel(null, selectMode) }
    }
    if (!allowEqual) {
      if (isDayEqual(dayObj, startState.value) || isDayEqual(dayObj, endState.value)) {
        return null
      }
    }
    if (startState.value && endState.value) {
      return {
        start: toModel(setTime(dayObj, startTime.value), selectMode),
        end: toModel(null, selectMode),
      }
    }
    const left = startState.value || dayObj
    const right = startState.value ? dayObj : null
    return {
      start: toModel(
        setTime(left, startTime.value),
        selectMode,
      ),
      end: toModel(
        setTime(right, endTime.value),
        selectMode,
      ),
    }
  }

  return {
    startTime,
    endTime,
    startState,
    startStr,
    endState,
    endStr,
    base,
    setModel,
    toModel,
    toRangeModel,
  }
}
